import React, { useState, useRef, Fragment, lazy, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, TagIcon, ChevronDownIcon, ExternalLinkIcon, NewspaperIcon, TrophyIcon, InstagramIcon, FacebookIcon, PlayCircleIcon, XIcon, Share2Icon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost } from '../data/blog-posts';
// ============================================================
//  Blog posts data is now in data/blog-posts.ts
//  Edit that file to add or modify blog posts.
// ============================================================
const ALL_CATEGORIES = ['All', 'Political', 'Immigration', 'Business', 'Note Worthy', 'Events', 'Arts & Entertainment', 'Sports'];
const CATEGORY_COLORS: Record<string, string> = {
  Political: 'bg-haa-blue text-white',
  Immigration: 'bg-haa-skyblue text-white',
  Business: 'bg-emerald-600 text-white',
  'Note Worthy': 'bg-haa-midblue text-white',
  'Arts & Entertainment': 'bg-haa-navy text-white',
  Events: 'bg-blue-400 text-white',
  Sports: 'bg-orange-500 text-white'
};
const GRADIENT_FALLBACKS: Record<string, string> = {
  Political: 'from-haa-blue via-blue-700 to-haa-navy',
  Immigration: 'from-haa-skyblue to-haa-midblue',
  Business: 'from-emerald-500 to-emerald-800',
  'Note Worthy': 'from-haa-midblue to-haa-navy',
  'Arts & Entertainment': 'from-haa-navy to-blue-950',
  Events: 'from-blue-400 to-haa-midblue',
  Sports: 'from-orange-500 to-red-600'
};
function getCategoryColor(category: string) {
  return CATEGORY_COLORS[category] ?? 'bg-haa-blue text-white';
}
function getGradient(category: string) {
  return GRADIENT_FALLBACKS[category] ?? 'from-haa-blue to-haa-navy';
}
// Story Modal Component
function StoryModal({
  isOpen,
  onClose,
  post,
  translatedTitle,
  translatedDate,
  translatedFullStory,
  isEs,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost;
  translatedTitle: string;
  translatedDate: string;
  translatedFullStory?: string;
  isEs: boolean;
  t: any;
}) {
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  return <AnimatePresence data-id="element-2871" data-mp="3ookknl1nfuup">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-2872" data-mp="3opn8fk3odh6e">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2873" data-mp="35w9n86prkp3z">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-2874" data-mp="1vr6u2olkf7r4">
              <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-2875" data-mp="1rshkz87k8cda">
                {post.category}
              </span>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2876" data-mp="1d1quq4lm9lca">
                <XIcon className="w-5 h-5" data-id="element-2877" data-mp="140k9xjbxpod8" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" data-id="element-2878" data-mp="1ssrjsq2vmk3g">
              {post.image && !(post.players && post.players.length > 0) && <div className="w-full h-80 sm:h-96 relative" data-id="element-2879" data-mp="1xsnn0n3r27hy">
                  <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover" style={{
              objectPosition: post.imagePosition || 'center top'
            }} data-id="element-2880" data-mp="1jyd78v9qdno1" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-2881" data-mp="1f0uyp5zmlagk" />
                </div>}

              {/* Video Embeds — shown at top when no image */}
              {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="w-full" data-id="element-2882" data-mp="2vf396274ftbu">
                    {post.videoEmbeds.map((video, vi) => <div key={vi} className="bg-black relative" data-id="element-2883" data-mp="2zdsqu6z6zx1a" data-mpi={vi}>
                        {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-2884" data-mp="jqyccd2cjvy3">
                            <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-2885" data-mp="sqlfb3aq59t9" />
                            {/* Fallback thumbnail link */}
                            <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-2886" data-mp="1m6ieha7wern9">
                              <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-2887" data-mp="2541pgjdx2kcb" />
                              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-2888" data-mp="2ivzou2htt1va">
                                <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-2889" data-mp="2nvuigdszk5k1" />
                                <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-2890" data-mp="c8iq1izp6ll9">
                                  Watch on YouTube
                                </span>
                              </div>
                            </a>
                          </div> : null}
                      </div>)}
                  </div>}

              <div className="p-6 sm:p-8" data-id="element-2891" data-mp="uzrvnh6ckq7k">
                <div className="flex items-center gap-2 mb-4" data-id="element-2892" data-mp="qw3rl8gxccr2">
                  <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-2893" data-mp="m5na9d9zg6zf" />
                  <span className="font-nunito text-gray-500 text-sm font-semibold" data-id="element-2894" data-mp="m3sqi2ag6kwx">
                    {translatedDate}
                  </span>
                </div>

                <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-6" data-id="element-2895" data-mp="1emznohsexu44">
                  {translatedTitle}
                </h2>

                {translatedFullStory && <div className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line mb-8" data-id="element-2896" data-mp="i6v5nmk6cxx4">
                    {translatedFullStory}
                  </div>}

                {/* Video Embeds — shown after story when post has image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8 space-y-4" data-id="element-2897" data-mp="10yxmaxwtge7d">
                      {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black relative" data-id="element-2898" data-mp="15uv5dbzgrynu" data-mpi={vi}>
                          {video.youtubeId ? <div className="relative w-full" style={{
                  paddingBottom: '56.25%'
                }} data-id="element-2899" data-mp="3cn5ns0bhycf2">
                              <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                    border: 0
                  }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-2900" data-mp="393wv5ydkb108" />
                              <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-2901" data-mp="27nqn391d2xkm">
                                <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-2902" data-mp="2bji7g3zqaq0b" />
                                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-2903" data-mp="1wu2kkz3bj4pw">
                                  <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-2904" data-mp="1sts4gtltaxxb" />
                                  <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-2905" data-mp="1nwa2xoa4jkzc">
                                    Watch on YouTube
                                  </span>
                                </div>
                              </a>
                            </div> : null}
                        </div>)}
                    </div>}

                {/* Story Images Gallery */}
                {post.storyImages && post.storyImages.length > 0 && <div className="mb-8 space-y-4" data-id="element-2906" data-mp="1ip3bq8c7mbib">
                    {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-2907" data-mp="l3o52fwz6sze" data-mpi={idx}>
                        <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-2908" data-mp="uzr43cwj6bi4" />
                      </div>)}
                  </div>}

                {/* Players */}
                {post.players && post.players.length > 0 && <div className="space-y-3 mb-8" data-id="element-2909" data-mp="3ve37kf4bzbgp">
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-2910" data-mp="3vcs5cjqjabrp">
                      {isEs ? 'Jugadores Destacados' : 'Featured Players'}
                    </h3>
                    {post.players.map(player => {
                const isExpanded = expandedPlayer === player.name;
                const playerKeyMap: Record<string, string> = {
                  'Oscar Avilez': 'avilez',
                  'David Ruíz': 'ruiz',
                  'Joseph Rosales': 'rosales',
                  'Andy Nájar': 'najar',
                  'Bryan Acosta': 'acosta'
                };
                const playerKey = playerKeyMap[player.name] ?? '';
                const translatedBio = playerKey ? t(`news.player.${playerKey}.bio`) : player.bio;
                const translatedPosition = player.position === 'Midfielder' ? t('news.player.position.midfielder') : player.position === 'Defender' ? t('news.player.position.defender') : player.position;
                return <div key={player.name} className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50" data-id="element-2911" data-mp="zalyoey89wb8">
                          <button onClick={() => setExpandedPlayer(isExpanded ? null : player.name)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-100 transition-colors text-left" aria-expanded={isExpanded} data-id="element-2912" data-mp="1rx6p9d6vq4dq">
                            {player.image ? <img src={player.image} alt={player.name} className="w-16 h-16 rounded-full object-cover object-top shadow-md flex-shrink-0 border-2 border-white" data-id="element-2913" data-mp="2aqkdkzg37ckd" /> : <div className={`w-16 h-16 rounded-full ${player.teamColor} flex items-center justify-center text-white font-nunito font-bold text-lg shadow-md flex-shrink-0`} data-id="element-2914" data-mp="91eqw58fwz22">
                                {player.name.split(' ').map(n => n[0]).join('')}
                              </div>}
                            <div className="flex-1 min-w-0" data-id="element-2915" data-mp="1oif3h24kijb2">
                              <p className="font-nunito text-sm font-bold text-haa-navy leading-tight" data-id="element-2916" data-mp="1jqonafiy91bq">
                                {player.name}
                                <span className="text-gray-400 font-normal ml-1.5" data-id="element-2917" data-mp="32eznxzfabiva">
                                  #{player.number}
                                </span>
                              </p>
                              <p className="font-nunito text-xs text-gray-500 mt-0.5" data-id="element-2918" data-mp="3bcjtvlo3e3c8">
                                {translatedPosition} · {player.team}
                              </p>
                            </div>
                            <motion.span animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex-shrink-0" data-id="element-2919" data-mp="3lagbmwbjbwjb">
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-2920" data-mp="7v9xr2sphvlf" />
                            </motion.span>
                          </button>
                          <AnimatePresence initial={false} data-id="element-2921" data-mp="290nk3kc91o07">
                            {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.25,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="overflow-hidden" data-id="element-2922" data-mp="241kh05ftxruv">
                                <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-2923" data-mp="12d9xty1k8et0">
                                  <p className="font-nunito text-sm text-gray-600 leading-relaxed" data-id="element-2924" data-mp="3dzamuu5bet17">
                                    {translatedBio}
                                  </p>
                                  <a href={player.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue transition-colors" data-id="element-2925" data-mp="34868nwngrc7f">
                                    {t('news.player.viewProfile')}
                                    <ExternalLinkIcon className="w-3 h-3" data-id="element-2926" data-mp="2q9ps7dzsu7c3" />
                                  </a>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>}

                {/* Video Embeds — shown after story when there is an image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8" data-id="element-2927" data-mp="2tphm5ir7fd9d">
                      <div className="flex items-center gap-2 mb-4" data-id="element-2928" data-mp="2tmlozwn6kslz">
                        <PlayCircleIcon className="w-5 h-5 text-amber-600" data-id="element-2929" data-mp="2oqeh3yb8571e" />
                        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider" data-id="element-2930" data-mp="2knrwahcz0mdp">
                          {isEs ? 'Videos' : 'Videos'}
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2931" data-mp="2aqn86bzkegu9">
                        {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black" data-id="element-2932" data-mp="dwsdrhr9iw8b" data-mpi={vi}>
                            {video.youtubeId ? <div className="relative w-full" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-2933" data-mp="lgt8zu3kdeag">
                                <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-2934" data-mp="v64df3fi9us0" />
                              </div> : video.facebookUrl ? <a href={video.facebookUrl} target="_blank" rel="noopener noreferrer" className="relative block w-full bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 transition-colors" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-2935" data-mp="1nuifbu9ptb8u">
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" data-id="element-2936" data-mp="2fcv831emevr7">
                                  <FacebookIcon className="w-8 h-8 text-blue-400" data-id="element-2937" data-mp="2kbygmvtt7k3x" />
                                  <span className="font-nunito text-white text-sm font-semibold" data-id="element-2938" data-mp="2oadzcniuy6bj">
                                    {video.label}
                                  </span>
                                  <span className="font-nunito text-white/50 text-xs" data-id="element-2939" data-mp="3kya3wzaepo0">
                                    {isEs ? 'Ver en Facebook' : 'Watch on Facebook'}
                                  </span>
                                </div>
                              </a> : null}
                            <div className="px-4 py-3 bg-gray-900" data-id="element-2940" data-mp="8x54pnmljuvv">
                              <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-2941" data-mp="4xwdapf1493h">
                                {video.label}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {/* Social Links */}
                {post.socialLinks && post.socialLinks.length > 0 && <div className="mb-8 pt-6 border-t border-gray-100" data-id="element-2942" data-mp="3i5ppgkiedrbb">
                    <h3 className="font-bebas text-xl text-haa-navy tracking-wider mb-4" data-id="element-2943" data-mp="1kcjy6hflk4m8">
                      {isEs ? 'Síguelos' : 'Follow Them'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-2944" data-mp="39uqnofx9hy6n">
                      {post.socialLinks.map((social, si) => <a key={si} href={social.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito text-sm font-bold transition-all duration-200 ${social.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white hover:shadow-lg hover:shadow-pink-500/25 hover:scale-105' : 'bg-[#1877F2] text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105'}`} aria-label={social.label} data-id="element-2945" data-mp="3en0a3lz64g46" data-mpi={si}>
                          {social.platform === 'instagram' ? <InstagramIcon className="w-4 h-4" data-id="element-2946" data-mp="2d599fck7r3tc" /> : <FacebookIcon className="w-4 h-4" data-id="element-2947" data-mp="24nfrw7ch8v5d" />}
                          {social.label}
                        </a>)}
                    </div>
                  </div>}

                {/* Coverage Links */}
                {post.coverageLinks && post.coverageLinks.length > 0 && <div className="pt-6 border-t border-gray-100" data-id="element-2948" data-mp="29kpktwz2roxy">
                    <div className="flex items-center gap-2 mb-3" data-id="element-2949" data-mp="1lxzlx4i0wahk">
                      <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-2950" data-mp="1gyn1eqemjfi5" />
                      <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-2951" data-mp="1gwaahnqw1usy">
                        {t('news.alsoCoveredBy')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-2952" data-mp="136gkv37k76i6">
                      {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200" data-id="element-2953" data-mp="y6lwp7f64v0t" data-mpi={i}>
                          {link.label}
                          <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-2954" data-mp="k5omar6bcni8" />
                        </a>)}
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// Featured Card
function FeaturedCard({
  post
}: {
  post: BlogPost;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postIdMap: Record<string, string> = {
    'post-1': '1',
    'post-7': '7',
    'post-2': '2',
    'post-5': '5',
    'post-6': '6',
    'post-8': '8',
    'post-9': '9',
    'post-10': '10',
    'post-11': '11',
    'post-12': '12',
    'post-13': '13',
    'post-14': '14',
    'post-15': '15',
    'post-16': '16',
    'post-17': '17',
    'post-18': '18',
    'post-19': '19',
    'post-20': '20',
    'post-21': '21',
    'post-22': '22',
    'post-23': '23',
    'post-24': '24',
    'post-25': '25',
    'post-26': '26',
    'post-27': '27',
    'post-28': '28',
    'post-29': '29',
    'post-30': '30',
    'post-31': '31',
    'post-32': '32',
    'post-33': '33',
    'post-34': '34',
    'post-35': '35',
    'post-36': '36',
    'post-37': '37'
  };
  const postNum = postIdMap[post.id] ?? '';
  const titleKey = postNum ? `news.post${postNum}.title` : '';
  const titleTranslation = titleKey ? t(titleKey) : '';
  const translatedTitle = titleKey && titleTranslation !== titleKey && titleTranslation !== '' ? titleTranslation : post.title;
  const excerptKey = postNum ? `news.post${postNum}.excerpt` : '';
  const excerptTranslation = excerptKey ? t(excerptKey) : '';
  const translatedExcerpt = excerptKey && excerptTranslation !== excerptKey ? excerptTranslation : post.excerpt;
  const dateKey = postNum ? `news.post${postNum}.date` : '';
  const dateTranslation = dateKey ? t(dateKey) : '';
  const translatedDate = dateKey && dateTranslation !== dateKey ? dateTranslation : post.date;
  const fullStoryKey = postNum ? `news.post${postNum}.fullStory` : '';
  const fullStoryTranslation = fullStoryKey ? t(fullStoryKey) : '';
  const translatedFullStory = fullStoryKey && fullStoryTranslation !== fullStoryKey ? fullStoryTranslation : post.fullStory;
  const hasStoryUrl = post.storyUrl && post.storyUrl !== '#';
  const hasFullStory = !!post.fullStory || !!post.players || !!post.videoEmbeds;
  const categoryKeyMap: Record<string, string> = {
    Political: 'news.catPolitical',
    Immigration: 'news.catImmigration',
    Business: 'news.catBusiness',
    'Note Worthy': 'news.catNoteWorthy',
    'Arts & Entertainment': 'news.catArtMedia',
    Events: 'news.catEvents',
    Sports: 'news.catSports'
  };
  const translatedCategory = categoryKeyMap[post.category] ? t(categoryKeyMap[post.category]) : post.category;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-xl lg:col-span-2 group" data-id="element-2955" data-mp="q0ymsakl8k7y">
      <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-2956" data-mp="18tj36sj3owuv">
        {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition || 'center 25%'
      }} data-id="element-2957" data-mp="13upmkr8iopqh" /> : !post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && post.videoEmbeds[0].youtubeId ? <iframe src={`https://www.youtube.com/embed/${post.videoEmbeds[0].youtubeId}`} title={post.videoEmbeds[0].label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-2958" data-mp="2p3sgjw8o93vm" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-2959" data-mp="267amv9a9itwq">
            <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 30% 70%, white 1px, transparent 1px)',
          backgroundSize: '25px 25px'
        }} data-id="element-2960" data-mp="18qvdraxqscwq" />
          </div>}
        {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-2961" data-mp="w7zm2tujw7s0" />}
        <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2962" data-mp="s6w8kdzz6nty">
          <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)} mb-3 inline-block`} data-id="element-2963" data-mp="n8uobpqtcigw">
            {translatedCategory}
          </span>
          {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <p className="font-bebas text-white text-3xl sm:text-4xl leading-tight tracking-wider line-clamp-2" data-id="element-2964" data-mp="8hlxxef36t2p">
              {translatedTitle}
            </p>}
        </div>
      </div>

      <div className="p-6 sm:p-8" data-id="element-2965" data-mp="3vqil35lh0xvp">
        <div className="flex items-center gap-3 mb-4" data-id="element-2966" data-mp="34lk271wxa6pp">
          <div className="w-8 h-1 bg-haa-blue rounded-full" data-id="element-2967" data-mp="34o67sw096c5a" />
          <span className="font-nunito text-haa-blue text-xs font-bold uppercase tracking-widest" data-id="element-2968" data-mp="2zmgt96dtngcg">
            {t('news.featuredStory')}
          </span>
        </div>

        <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-3" data-id="element-2969" data-mp="39kwnhi85b5q6">
          {translatedTitle}
        </h2>

        <div className="mb-6" data-id="element-2970" data-mp="2lw50r6dbrfks">
          <p className="font-nunito text-gray-600 leading-relaxed" data-id="element-2971" data-mp="2gwsg8s9xekld">
            {translatedExcerpt}
          </p>

          {/* Story preview — show first 2 paragraphs of fullStory */}
          {translatedFullStory && <div className="relative mt-4" data-id="element-2972" data-mp="2quezts2670bo">
              <div className="font-nunito text-gray-500 text-sm leading-relaxed space-y-3" data-id="element-2973" data-mp="2qtndz8ydkgyl">
                {translatedFullStory.split('\n\n').slice(0, 2).map((para, i) => <p key={i} data-id="element-2974" data-mp="doibimrstxl0" data-mpi={i}>{para}</p>)}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-2975" data-mp="dmfbrb0ugq33" />
            </div>}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5" data-id="element-2976" data-mp="3rdlgbuu1hyx">
            {hasStoryUrl ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-2977" data-mp="8povaml4nsh5">
                {t('news.readFullStory')}
                <ExternalLinkIcon className="w-4 h-4" data-id="element-2978" data-mp="reby0yfu05rk" />
              </a> : hasFullStory ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-2979" data-mp="382pm32lxd0ah">
                {t('news.readFullStory')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-2980" data-mp="18xc5t4g0r3lz" />
              </button> : null}

            {post.coverageLinks && post.coverageLinks.length > 0 && <>
                <span className="text-gray-200 select-none hidden sm:inline" data-id="element-2981" data-mp="13w68sufbsu6n">
                  |
                </span>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-2982" data-mp="uxubk9jyey58">
                  <NewspaperIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-2983" data-mp="uznkxwlybwgx" />
                  <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-2984" data-mp="pyzrz5e5x5k2">
                    {t('news.alsoCoveredBy')}
                  </span>
                  {post.coverageLinks.map((link, i) => <Fragment key={link.url} data-id="element-2985" data-mp="2fdnv0g0v5b5f" data-mpi={i}>
                      {i > 0 && <span className="text-gray-200 select-none" data-id="element-2986" data-mp="2fbl8w72u025z">·</span>}
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-2987" data-mp="2nslt11xi0xoh">
                        {link.label}
                        <ExternalLinkIcon className="w-3 h-3" data-id="element-2988" data-mp="24xnbbtdpqclk" />
                      </a>
                    </Fragment>)}
                </div>
              </>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-2989" data-mp="2dyle68cf0e2k">
          <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm" data-id="element-2990" data-mp="2iwn154cyoxjb">
            <span className="flex items-center gap-1.5" data-id="element-2991" data-mp="3paw13y4h9fz3">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-2992" data-mp="3u9z7blw71s8n" />
              <span className="font-nunito" data-id="element-2993" data-mp="3ucldpi7cm1pa">{translatedDate}</span>
            </span>
          </div>
          <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1.5 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-2994" data-mp="1745m9ajktgbf">
            <Share2Icon className="w-3.5 h-3.5" data-id="element-2995" data-mp="1b2bllxi34tmb" />
            {isEs ? 'Compartir' : 'Share'}
          </a>
        </div>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-2996" data-mp="1l1u2u66m21ao" />
    </motion.article>;
}
// Regular Card
function PostCard({
  post,
  index
}: {
  post: BlogPost;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postIdMap: Record<string, string> = {
    'post-1': '1',
    'post-7': '7',
    'post-2': '2',
    'post-5': '5',
    'post-6': '6',
    'post-8': '8',
    'post-9': '9',
    'post-10': '10',
    'post-11': '11',
    'post-12': '12',
    'post-13': '13',
    'post-14': '14',
    'post-15': '15',
    'post-16': '16',
    'post-17': '17',
    'post-18': '18',
    'post-19': '19',
    'post-20': '20',
    'post-21': '21',
    'post-22': '22',
    'post-23': '23',
    'post-24': '24',
    'post-25': '25',
    'post-26': '26',
    'post-27': '27',
    'post-28': '28',
    'post-29': '29',
    'post-30': '30',
    'post-31': '31',
    'post-32': '32',
    'post-33': '33',
    'post-34': '34',
    'post-35': '35',
    'post-36': '36',
    'post-37': '37'
  };
  const postNum = postIdMap[post.id] ?? '';
  const titleKey = postNum ? `news.post${postNum}.title` : '';
  const titleTranslation = titleKey ? t(titleKey) : '';
  const translatedTitle = titleKey && titleTranslation !== titleKey && titleTranslation !== '' ? titleTranslation : post.title;
  const excerptKey = postNum ? `news.post${postNum}.excerpt` : '';
  const excerptTranslation = excerptKey ? t(excerptKey) : '';
  const translatedExcerpt = excerptKey && excerptTranslation !== excerptKey ? excerptTranslation : post.excerpt;
  const dateKey = postNum ? `news.post${postNum}.date` : '';
  const dateTranslation = dateKey ? t(dateKey) : '';
  const translatedDate = dateKey && dateTranslation !== dateKey ? dateTranslation : post.date;
  const readTimeKey = postNum ? `news.post${postNum}.readTime` : '';
  const readTimeTranslation = readTimeKey ? t(readTimeKey) : '';
  const translatedReadTime = readTimeKey && readTimeTranslation !== readTimeKey ? readTimeTranslation : post.readTime;
  const fullStoryKey = postNum ? `news.post${postNum}.fullStory` : '';
  const fullStoryTranslation = fullStoryKey ? t(fullStoryKey) : '';
  const translatedFullStory = fullStoryKey && fullStoryTranslation !== fullStoryKey ? fullStoryTranslation : post.fullStory;
  const categoryKeyMap: Record<string, string> = {
    Political: 'news.catPolitical',
    Immigration: 'news.catImmigration',
    Business: 'news.catBusiness',
    'Note Worthy': 'news.catNoteWorthy',
    'Arts & Entertainment': 'news.catArtMedia',
    Events: 'news.catEvents',
    Sports: 'news.catSports'
  };
  const translatedCategory = categoryKeyMap[post.category] ? t(categoryKeyMap[post.category]) : post.category;
  const isSportsComposite = post.id === 'post-8' || post.players && post.players.length > 0;
  const isWabafu = post.id === 'post-12';
  const compositeImages = [{
    name: 'Oscar Avilez',
    url: 'https://cdn.magicpatterns.com/uploads/hkEmYVyvz28kSzeT5ukNrR/Oscar_AViles.png'
  }, {
    name: 'David Ruíz',
    url: 'https://cdn.magicpatterns.com/uploads/jX1nHu27oh4QsStqz4qtmu/David_ruiz.png'
  }, {
    name: 'Joseph Rosales',
    url: 'https://cdn.magicpatterns.com/uploads/qGfb5k5pbqZyGMPg5qDGSp/Joseph_Rosales.png'
  }, {
    name: 'Andy Nájar',
    url: 'https://cdn.magicpatterns.com/uploads/mQoPkEXd6jyhojvo6LYyV9/Any_Najar.png'
  }, {
    name: 'Bryan Acosta',
    url: 'https://cdn.magicpatterns.com/uploads/3zNFYQqGHoBrp1dCyKXGuJ/Bryan_Acosta.png'
  }];
  const hasMultipleImages = !!post.secondImage;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-lg group flex flex-col" data-id="element-2997" data-mp="1ip9pi7ht4k2n">
      {isSportsComposite ? <div className="relative h-64 overflow-hidden flex-shrink-0 bg-gradient-to-b from-haa-midblue via-haa-blue to-haa-navy" data-id="element-2998" data-mp="owk9ohwjlsos">
          {/* Player Images */}
          <div className="absolute inset-0 flex items-end justify-center px-2 pb-12 sm:pb-10" data-id="element-2999" data-mp="twou2j0kn7ka">
            {compositeImages.map((img, i) => <motion.div key={img.name} initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + i * 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-1/5 -mx-1.5 sm:-mx-2" data-id="element-3000" data-mp="ku753wtj80tl" data-mpi={i}>
                <img src={img.url} alt={img.name} className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-3001" data-mp="1wadu61g4q8a8" />
              </motion.div>)}
          </div>
          {/* Gradient Overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-haa-navy/80 to-transparent" data-id="element-3002" data-mp="1qzzfz92f30or" />
          {/* Top Left Badge */}
          <div className="absolute top-3 left-3" data-id="element-3003" data-mp="1h57i044xabtg">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3004" data-mp="24tqnyd0i2cid">
              {translatedCategory}
            </span>
          </div>
          {/* Bottom Text */}
          <div className="absolute bottom-0 left-0 right-0 p-5 pb-3" data-id="element-3005" data-mp="1w16mz5wqjvud">
            <p className="text-white/90 font-nunito text-[10px] sm:text-xs font-bold tracking-widest mb-1 flex items-center gap-1.5" data-id="element-3006" data-mp="201yxs3l9a8sr">
              <span className="text-lg leading-none" data-id="element-3007" data-mp="2nqi4ieonqdiq">🇭🇳</span>
              {isEs ? 'HONDUREÑOS EN LA MLS · TEMPORADA 2025-26' : 'HONDURANS IN MLS · 2025-26 SEASON'}
            </p>
          </div>
        </div> : isWabafu ? <div className="relative h-52 overflow-hidden flex-shrink-0" data-id="element-3008" data-mp="c4hm41feqxji">
          <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center center'
      }} data-id="element-3009" data-mp="quovlrnft682" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-3010" data-mp="3pcav5nnhbzn4" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-3011" data-mp="83xpcyyqxvxw">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={inView ? {
          scale: 1,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-3012" data-mp="34b9sivg43lx">
              <span className="text-5xl mb-3 block" data-id="element-3013" data-mp="1qo3w6894u6v0">🥁</span>
              <p className="font-bebas text-white text-2xl sm:text-3xl tracking-wider leading-tight" data-id="element-3014" data-mp="1vmf94ruq4xhg">
                {isEs ? 'CULTURA EN MOVIMIENTO' : 'CULTURE IN MOTION'}
              </p>
              <p className="font-nunito text-white/70 text-xs tracking-widest uppercase mt-1" data-id="element-3015" data-mp="12xhset65v8qt">
                Wabafu Garifuna Dance Theater
              </p>
            </motion.div>
          </div>
          <div className="absolute top-3 left-3" data-id="element-3016" data-mp="1ctl3ioizqhpw">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3017" data-mp="1csrs3a93741f">
              {translatedCategory}
            </span>
          </div>
        </div> : hasMultipleImages ? <div className="flex-shrink-0" data-id="element-3018" data-mp="p2xgew0i7lir">
          <div className="grid grid-cols-2 gap-0.5" data-id="element-3019" data-mp="32ftiz184q8hd">
            <div className="relative h-36 overflow-hidden" data-id="element-3020" data-mp="32d7cl4wz5z0q">
              <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: post.imagePosition ?? 'center 15%'
          }} data-id="element-3021" data-mp="2yerhseunjmcr" />
            </div>
            <div className="relative h-36 overflow-hidden" data-id="element-3022" data-mp="2jia8dt60whsu">
              <img src={post.secondImage!} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: 'center center'
          }} data-id="element-3023" data-mp="2eoxlbngzsj27" />
            </div>
          </div>
          <div className="relative -mt-8 px-3 pb-0 z-10" data-id="element-3024" data-mp="1vquynfbx2y7k">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3025" data-mp="1vrejb3syc0pa">
              {translatedCategory}
            </span>
          </div>
        </div> : <div className="relative h-44 overflow-hidden flex-shrink-0" data-id="element-3026" data-mp="3m42wjnvfh3ht">
          {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center 15%'
      }} data-id="element-3027" data-mp="3r1m9mkge3a86" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3028" data-mp="3360572fequgu">
              <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-3029" data-mp="385d0mbkch9v1" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-3030" data-mp="2lvmpsyz7el93" />
          <div className="absolute top-3 left-3" data-id="element-3031" data-mp="30jr13hvxfuhu">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3032" data-mp="30llod2uo7yp3">
              {translatedCategory}
            </span>
          </div>
        </div>}

      <div className="p-5 flex flex-col flex-1" data-id="element-3033" data-mp="35jvfn2d9yf5f">
        <div className="flex items-center gap-2 mb-2" data-id="element-3034" data-mp="35l6jf26pzmwj">
          <div className="w-4 h-0.5 bg-haa-blue rounded-full" data-id="element-3035" data-mp="3jhtt85jru7jv" />
          <span className="font-nunito text-gray-400 text-xs" data-id="element-3036" data-mp="3ejs9rjifo67v">
            {translatedDate}
          </span>
        </div>

        {!isSportsComposite && <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight mb-2" data-id="element-3037" data-mp="1p5doxaajd3hn">
            {translatedTitle}
          </h3>}

        <div className="flex-1 mb-3" data-id="element-3038" data-mp="3k8a4ns49swnz">
          {!isSportsComposite && <p className="font-nunito text-gray-500 text-sm leading-relaxed line-clamp-3" data-id="element-3039" data-mp="3kad2uzfktw3s">
              {translatedExcerpt}
            </p>}
        </div>

        <div className="mb-4" data-id="element-3040" data-mp="3t75a31zhq2q8">
          {post.storyUrl && post.storyUrl !== '#' ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3041" data-mp="23gp5tvfspsq">
              {t('news.readFullStory')}
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3042" data-mp="krln2kpexgjm" />
            </a> : post.fullStory || post.players || post.videoEmbeds ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3043" data-mp="pq5ayqnkh2em">
              {t('news.readFullStory')}
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3044" data-mp="uxlif2gq54o5" />
            </button> : null}
        </div>

        {post.coverageLinks && post.coverageLinks.length > 0 && <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3" data-id="element-3045" data-mp="1nlr17jo7wjlc">
            <NewspaperIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-3046" data-mp="1imo7gvdotzso" />
            <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3047" data-mp="1iohdq9ke2i05">
              {t('news.alsoCoveredBy')}
            </span>
            {post.coverageLinks.map((link, i) => <Fragment key={i} data-id="element-3048" data-mp="26bz17swmetxb" data-mpi={i}>
                {i > 0 && <span className="text-gray-200 select-none" data-id="element-3049" data-mp="26csbh3usg1k7">·</span>}
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3050" data-mp="21kqr0ungv4j3">
                  {link.label}
                  <ExternalLinkIcon className="w-3 h-3" data-id="element-3051" data-mp="2bcocwwdzmd4t" />
                </a>
              </Fragment>)}
          </div>}
      </div>

      <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-gray-100 mt-auto" data-id="element-3052" data-mp="34021o77j82du">
        <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs" data-id="element-3053" data-mp="38zevxd0qgbqg">
          <span className="flex items-center gap-1" data-id="element-3054" data-mp="2u1mh6n30frdb">
            <CalendarIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-3055" data-mp="2u12z96dd12za" />
            <span className="font-nunito" data-id="element-3056" data-mp="2yzwg98ruvc47">{translatedDate}</span>
          </span>
        </div>
        <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3057" data-mp="1ilxvs05en0em">
          <Share2Icon className="w-3.5 h-3.5" data-id="element-3058" data-mp="5pqip927bsra" />
          {isEs ? 'Compartir' : 'Share'}
        </a>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3059" data-mp="eoc0v33rh5rj" />
    </motion.article>;
}
// Main Section
export function NewsBlog() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    t
  } = useLanguage();
  const filtered = activeCategory === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === activeCategory);
  const featured = filtered.find(p => p.featured) ?? null;
  const rest = filtered.filter(p => p.id !== featured?.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const categoryTranslations: Record<string, string> = {
    All: t('news.catAll'),
    Political: t('news.catPolitical'),
    Immigration: t('news.catImmigration'),
    Business: t('news.catBusiness'),
    'Note Worthy': t('news.catNoteWorthy'),
    Events: t('news.catEvents'),
    'Arts & Entertainment': t('news.catArtMedia'),
    Sports: t('news.catSports')
  };
  return <section id="news" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="news-heading" data-id="element-3060" data-mp="2bch6hnfspc8h">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3061" data-mp="22dwt91t07urn">
        <div ref={titleRef} className="mb-10" data-id="element-3062" data-mp="22cc2ln9t8vz2">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-3063" data-mp="1xf2b822v2a8l">
            <div data-id="element-3064" data-mp="gv2uzj3jiqf7">
              <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3065" data-mp="gsgo7loh4ey1">
                {t('news.label')}
              </span>
              <h2 id="news-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3066" data-mp="qm5xmpc4bdgl">
                {t('news.heading')}
              </h2>
              <div className="flex items-center gap-3 mt-4" data-id="element-3067" data-mp="14ke01crxb6z5">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3068" data-mp="14kxay9j5ov3o" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3069" data-mp="19jqjronhzxxi" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3070" data-mp="36c0jmt9hjzll" />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Filter by category" data-id="element-3071" data-mp="3bbmyt88vjpx1">
          {ALL_CATEGORIES.map(cat => <button key={cat} role="tab" aria-selected={activeCategory === cat} onClick={() => setActiveCategory(cat)} className={`relative font-nunito font-semibold text-sm px-5 py-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue ${activeCategory === cat ? 'bg-haa-navy text-white shadow-md' : 'bg-white text-gray-500 hover:text-haa-navy border border-gray-200 hover:border-haa-navy'}`} data-id="element-3072" data-mp="3m773mh0zx8zx">
              <span className="inline-flex items-center gap-1.5" data-id="element-3073" data-mp="nh2bhep7mhra">
                {cat === 'Sports' && <TrophyIcon className="w-3.5 h-3.5" data-id="element-3074" data-mp="sfx39655ngnj" />}
                {categoryTranslations[cat] ?? cat}
              </span>
            </button>)}
        </motion.div>

        <AnimatePresence mode="wait" data-id="element-3075" data-mp="ei8jk1mtqfk2">
          <motion.div key={activeCategory} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-3076" data-mp="jephtonqmaav">
            {filtered.length === 0 ? <div className="text-center py-20" data-id="element-3077" data-mp="3vxfcni0ksvq2">
                <TagIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-3078" data-mp="3hwup68jpubq8" />
                <p className="font-nunito text-gray-400 text-lg" data-id="element-3079" data-mp="3hyxr9qy57vbb">
                  {t('news.noPosts')}
                </p>
              </div> : activeCategory === 'All' ? <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3080" data-mp="t5sk49ia51db">
                  {featured && <FeaturedCard post={featured} data-id="element-3081" data-mp="p35wkksn62lx" />}
                  {rest.length > 0 && <div className="flex flex-col gap-6" data-id="element-3082" data-mp="k9tcmnywqjzi">
                      {rest.slice(0, 2).map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3083" data-mp="faqgjt0wno3o" data-mpi={i} />)}
                    </div>}
                </div>
                {rest.length > 2 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-id="element-3084" data-mp="5gq2v2rk5ajc">
                    {rest.slice(2).map((post, i) => <PostCard key={post.id} post={post} index={i + 2} data-id="element-3085" data-mp="3sk4n9dql8p9d" data-mpi={i} />)}
                  </div>}
              </> : <div className={`grid gap-6 ${filtered.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`} data-id="element-3086" data-mp="1qrzqnoy593sb">
                {filtered.map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3087" data-mp="239k8bohar4xh" data-mpi={i} />)}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}